import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import {
  Plus, Search, Pencil, Trash2, Upload, Download, X,
} from 'lucide-react';
import { AccountingLayout } from '../components/AccountingLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  listPajak, pajakSummary, pajakReport, createPajak, updatePajak, deletePajak, importPajak,
} from '../lib/api';
import { formatRp, parseNumber, formatThousands, MONTHS_ID } from '../lib/format';
import { useAuth } from '../context/AuthContext';

const MARKETPLACES = ['SHOPEE', 'TOKOPEDIA', 'TIKTOK SHOP', 'LAINNYA'];
const MP_DOT = {
  SHOPEE: 'bg-orange-500',
  TOKOPEDIA: 'bg-emerald-600',
  'TIKTOK SHOP': 'bg-stone-900',
  LAINNYA: 'bg-stone-400',
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const monthLabel = (ym) => {
  const [y, m] = ym.split('-');
  return `${MONTHS_ID[parseInt(m, 10) - 1]} ${y}`;
};

const parseTanggalFlex = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') {
    const d = XLSX.SSF && XLSX.SSF.parse_date_code ? XLSX.SSF.parse_date_code(v) : null;
    if (d && d.y) return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
    return null;
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  return null;
};

const SummaryCard = ({ label, value, testid, accent = false }) => (
  <div className="card-modern p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
    <div className={`num text-xl font-semibold mt-1 ${accent ? 'text-emerald-800' : 'text-stone-900'}`}>
      {value}
    </div>
  </div>
);

export default function PajakMarketplacePage() {
  const { user } = useAuth();
  const now = new Date();
  const [tab, setTab] = useState('input');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [txs, setTxs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [mpFilter, setMpFilter] = useState('');
  const [form, setForm] = useState({ tanggal: todayISO(), marketplace: 'SHOPEE', no_order: '', pajak: '' });
  const [busy, setBusy] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [delTx, setDelTx] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState([]);
  const orderRef = useRef(null);
  const fileRef = useRef(null);
  const full = !!user?.opname_only;

  const reload = async () => {
    setLoading(true);
    try {
      const params = { month, year };
      if (mpFilter) params.marketplace = mpFilter;
      if (search) params.search = search;
      const [list, sum] = await Promise.all([listPajak(params), pajakSummary({ month, year })]);
      setTxs(list);
      setSummary(sum);
    } catch {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [month, year, mpFilter, search]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => {
    if (tab === 'laporan') {
      pajakReport().then(setReport).catch(() => toast.error('Gagal memuat laporan'));
    }
  }, [tab]);

  const totalPajak = useMemo(() => txs.reduce((a, t) => a + (t.pajak || 0), 0), [txs]);

  const submitForm = async (e) => {
    e.preventDefault();
    if (busy) return;
    const p = parseNumber(form.pajak);
    if (!form.tanggal) return toast.error('Tanggal tidak boleh kosong');
    if (!form.marketplace) return toast.error('Marketplace tidak boleh kosong');
    if (!form.no_order.trim()) { orderRef.current?.focus(); return toast.error('No. Order tidak boleh kosong'); }
    if (isNaN(p) || p < 0) return toast.error('Pajak harus angka dan tidak boleh negatif');
    setBusy(true);
    try {
      await createPajak({
        tanggal: form.tanggal, marketplace: form.marketplace,
        no_order: form.no_order.trim(), pajak: p,
      });
      toast.success('Transaksi tersimpan');
      setForm((f) => ({ ...f, no_order: '', pajak: '' }));
      orderRef.current?.focus();
      reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409) toast.warning(d || 'Nomor order ini sudah tercatat');
      else toast.error(typeof d === 'string' ? d : 'Gagal menyimpan');
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (t) => {
    setEditTx(t);
    setEditForm({
      tanggal: t.tanggal, marketplace: t.marketplace,
      no_order: t.no_order, pajak: formatThousands(t.pajak || 0),
    });
  };

  const saveEdit = async () => {
    if (!editTx || !editForm) return;
    const p = parseNumber(editForm.pajak);
    if (!editForm.tanggal) return toast.error('Tanggal tidak boleh kosong');
    if (!editForm.no_order.trim()) return toast.error('No. Order tidak boleh kosong');
    if (isNaN(p) || p < 0) return toast.error('Pajak harus angka dan tidak boleh negatif');
    try {
      await updatePajak(editTx.id, {
        tanggal: editForm.tanggal, marketplace: editForm.marketplace,
        no_order: editForm.no_order.trim(), pajak: p,
      });
      toast.success('Perubahan tersimpan');
      setEditTx(null);
      reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409) toast.warning(d || 'Nomor order ini sudah tercatat');
      else toast.error(typeof d === 'string' ? d : 'Gagal menyimpan');
    }
  };

  const doDelete = async () => {
    if (!delTx) return;
    try {
      await deletePajak(delTx.id);
      toast.success('Transaksi dihapus');
      setDelTx(null);
      reload();
    } catch {
      toast.error('Gagal menghapus');
    }
  };

  const pickImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      let hIdx = -1;
      let cols = {};
      for (let i = 0; i < Math.min(aoa.length, 10); i++) {
        const row = (aoa[i] || []).map((v) => String(v || '').trim().toLowerCase());
        const c = {};
        row.forEach((h, j) => {
          const n = h.replace(/[.\s_]+/g, '');
          if (c.tanggal === undefined && (n === 'tanggal' || n === 'tgl' || n === 'date')) c.tanggal = j;
          if (c.mp === undefined && (n === 'marketplace' || n === 'mp')) c.mp = j;
          if (c.order === undefined && (n === 'noorder' || n === 'order' || n === 'nopesanan' || n === 'noresi')) c.order = j;
          if (c.pajak === undefined && (n === 'pajak' || n === 'pph22' || n === 'pph')) c.pajak = j;
        });
        if (c.tanggal !== undefined && c.order !== undefined && c.pajak !== undefined) {
          hIdx = i; cols = c; break;
        }
      }
      if (hIdx < 0) {
        toast.error('Kolom TANGGAL / NO. ORDER / PAJAK tidak ditemukan di file');
        setImportData(null);
        return;
      }
      const valid = [];
      const errors = [];
      for (let i = hIdx + 1; i < aoa.length; i++) {
        const line = aoa[i];
        if (!line || line.every((v) => v === '' || v === null)) continue;
        const tgl = parseTanggalFlex(line[cols.tanggal]);
        const mp = cols.mp !== undefined ? String(line[cols.mp] ?? '').trim().toUpperCase() : '';
        const order = String(line[cols.order] ?? '').trim();
        const pjk = Number(line[cols.pajak]);
        if (!tgl) { errors.push({ row: i + 1, reason: 'Tanggal tidak valid' }); continue; }
        if (!MARKETPLACES.includes(mp)) { errors.push({ row: i + 1, reason: `Marketplace tidak dikenal: ${mp || '(kosong)'}` }); continue; }
        if (!order) { errors.push({ row: i + 1, reason: 'No. Order kosong' }); continue; }
        if (isNaN(pjk) || pjk < 0) { errors.push({ row: i + 1, reason: 'Pajak bukan angka valid' }); continue; }
        valid.push({ tanggal: tgl, marketplace: mp, no_order: order, pajak: Math.round(pjk) });
      }
      setImportData({ valid, errors, fileName: f.name });
    } catch (err) {
      toast.error(`Gagal membaca file: ${err.message || err}`);
      setImportData(null);
    }
  };

  const doImport = async () => {
    if (!importData?.valid?.length) return;
    setImporting(true);
    try {
      const r = await importPajak(importData.valid);
      toast.success(
        `Import selesai: ${r.inserted} tersimpan` +
        (r.skipped_duplicates ? `, ${r.skipped_duplicates} duplikat dilewati` : '')
      );
      if (r.errors?.length) toast.warning(`${r.errors.length} baris error dilewati`);
      setImportOpen(false);
      setImportData(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal import');
    } finally {
      setImporting(false);
    }
  };

  const headerStyle = { font: { bold: true, color: { rgb: 'FFFFFFFF' } }, fill: { fgColor: { rgb: 'FF065F46' } } };

  const doExport = () => {
    if (!txs.length) return toast.error('Tidak ada data untuk diekspor');
    const aoa = [
      ['TANGGAL', 'MARKETPLACE', 'NO. ORDER', 'PAJAK'].map((h) => ({ v: h, s: headerStyle })),
      ...txs.map((t) => [fmtDate(t.tanggal), t.marketplace, t.no_order,
        { v: t.pajak, t: 'n', s: { numFmt: '"Rp"#,##0' } }]),
      [],
      ['', '', 'TOTAL PAJAK', { v: totalPajak, t: 'n', s: { numFmt: '"Rp"#,##0', font: { bold: true } } }],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 12 }, { wch: 14 }, { wch: 26 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pajak');
    XLSX.writeFile(wb, `Pajak_Marketplace_${year}-${String(month).padStart(2, '0')}.xlsx`);
  };

  const doExportReport = () => {
    if (!report.length) return toast.error('Belum ada data laporan');
    const aoa = [
      ['BULAN', 'SHOPEE', 'TOKOPEDIA', 'TIKTOK SHOP', 'LAINNYA', 'TOTAL'].map((h) => ({ v: h, s: headerStyle })),
      ...report.map((m) => [
        monthLabel(m.ym),
        { v: m.shopee, t: 'n', s: { numFmt: '"Rp"#,##0' } },
        { v: m.tokopedia, t: 'n', s: { numFmt: '"Rp"#,##0' } },
        { v: m.tiktok, t: 'n', s: { numFmt: '"Rp"#,##0' } },
        { v: m.lainnya, t: 'n', s: { numFmt: '"Rp"#,##0' } },
        { v: m.total, t: 'n', s: { numFmt: '"Rp"#,##0', font: { bold: true } } },
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Laporan PPh 22');
    XLSX.writeFile(wb, 'Laporan_PPh22_Marketplace.xlsx');
  };

  return (
    <AccountingLayout fullWidth={full}>
      <div data-testid="pajak-page">
        {/* Header + filter bulan/tahun */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-2">
              PPh 22 Marketplace
            </div>
            <h1 className="font-heading text-3xl font-semibold text-stone-900 tracking-tight">
              Pajak Marketplace
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              Catatan PPh 22 dari Shopee, Tokopedia, TikTok Shop dan marketplace lainnya
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger className="bg-white w-36" data-testid="pajak-filter-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS_ID.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)} data-testid={`pajak-month-${i + 1}`}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number" value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10) || now.getFullYear())}
              className="bg-white w-28" data-testid="pajak-filter-year"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-stone-200 mb-5">
          <button onClick={() => setTab('input')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'input' ? 'border-emerald-800 text-emerald-800' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
            data-testid="pajak-tab-input">Pajak Marketplace</button>
          <button onClick={() => setTab('laporan')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'laporan' ? 'border-emerald-800 text-emerald-800' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
            data-testid="pajak-tab-laporan">Laporan</button>
        </div>

        {tab === 'input' && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
              <SummaryCard label="Total Pajak Bulan Ini" value={formatRp(summary?.total || 0)} accent testid="pajak-card-total" />
              <SummaryCard label="Shopee" value={formatRp(summary?.shopee || 0)} testid="pajak-card-shopee" />
              <SummaryCard label="Tokopedia" value={formatRp(summary?.tokopedia || 0)} testid="pajak-card-tokopedia" />
              <SummaryCard label="TikTok Shop" value={formatRp(summary?.tiktok || 0)} testid="pajak-card-tiktok" />
              <SummaryCard label="Total Transaksi" value={summary?.count ?? 0} testid="pajak-card-count" />
            </div>

            {/* Quick input form */}
            <div className="card-modern p-4 mb-4" data-testid="pajak-quick-form">
              <form onSubmit={submitForm} className="grid grid-cols-1 md:grid-cols-[170px_180px_1fr_160px_auto] gap-3 items-end">
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tanggal</Label>
                  <Input type="date" value={form.tanggal}
                    onChange={(e) => setForm({ ...form, tanggal: e.target.value })}
                    className="bg-white" data-testid="pajak-input-tanggal" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Marketplace</Label>
                  <Select value={form.marketplace} onValueChange={(v) => setForm({ ...form, marketplace: v })}>
                    <SelectTrigger className="bg-white" data-testid="pajak-input-marketplace">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MARKETPLACES.map((m) => (
                        <SelectItem key={m} value={m} data-testid={`pajak-mp-${m.replace(/\s/g, '-')}`}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">No. Order</Label>
                  <Input ref={orderRef} value={form.no_order}
                    onChange={(e) => setForm({ ...form, no_order: e.target.value })}
                    placeholder="Contoh: 261002GVCM1EA6" autoComplete="off"
                    className="bg-white font-mono" data-testid="pajak-input-order" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Pajak (Rp)</Label>
                  <Input type="text" inputMode="numeric" value={form.pajak}
                    onChange={(e) => setForm({ ...form, pajak: formatThousands(e.target.value) })}
                    placeholder="0" className="bg-white font-mono" data-testid="pajak-input-pajak" />
                </div>
                <Button type="submit" disabled={busy}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white h-10"
                  data-testid="pajak-add-btn">
                  <Plus className="w-4 h-4 mr-1" /> Tambah
                </Button>
              </form>
            </div>

            {/* Search + filter + import/export */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <div className="relative">
                <Search className="w-4 h-4 text-stone-400 absolute left-2.5 top-2.5" />
                <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Cari nomor order…" className="pl-9 h-9 w-64 bg-white"
                  data-testid="pajak-search" />
              </div>
              <Select value={mpFilter || 'ALL'} onValueChange={(v) => setMpFilter(v === 'ALL' ? '' : v)}>
                <SelectTrigger className="bg-white h-9 w-44" data-testid="pajak-filter-mp">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL" data-testid="pajak-filter-mp-all">Semua Marketplace</SelectItem>
                  {MARKETPLACES.map((m) => (
                    <SelectItem key={m} value={m} data-testid={`pajak-filter-mp-${m.replace(/\s/g, '-')}`}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}
                  className="border-stone-200" data-testid="pajak-import-btn">
                  <Upload className="w-4 h-4 mr-1" /> Import Excel
                </Button>
                <Button variant="outline" size="sm" onClick={doExport}
                  className="border-stone-200" data-testid="pajak-export-btn">
                  <Download className="w-4 h-4 mr-1" /> Export Excel
                </Button>
              </div>
            </div>

            {/* Tabel */}
            <div className="card-modern overflow-hidden" data-testid="pajak-table-card">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]" data-testid="pajak-table">
                  <thead>
                    <tr className="bg-stone-50 text-[11px] uppercase tracking-widest text-stone-500">
                      <th className="text-left px-4 py-3 w-12">No</th>
                      <th className="text-left px-4 py-3">Tanggal</th>
                      <th className="text-left px-4 py-3">Marketplace</th>
                      <th className="text-left px-4 py-3">No. Order</th>
                      <th className="text-right px-4 py-3">Pajak (Rp)</th>
                      <th className="text-right px-4 py-3 w-24">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txs.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-sm text-stone-400 py-10" data-testid="pajak-empty">
                          {loading ? 'Memuat…' : 'Belum ada transaksi pada periode ini'}
                        </td>
                      </tr>
                    )}
                    {txs.map((t, i) => (
                      <tr key={t.id} className="border-t border-stone-100 hover:bg-stone-50/60"
                        data-testid={`pajak-row-${t.id}`}>
                        <td className="px-4 py-2.5 text-sm text-stone-500 num">{i + 1}</td>
                        <td className="px-4 py-2.5 text-sm text-stone-700 num">{fmtDate(t.tanggal)}</td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-800">
                            <span className={`w-2 h-2 rounded-full ${MP_DOT[t.marketplace] || 'bg-stone-400'}`} />
                            {t.marketplace}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-sm font-mono text-stone-800">{t.no_order}</td>
                        <td className="px-4 py-2.5 text-sm text-right num font-semibold text-stone-900">
                          {formatRp(t.pajak)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <button onClick={() => openEdit(t)}
                            className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-emerald-800 transition-colors"
                            data-testid={`pajak-edit-${t.id}`} title="Edit">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => setDelTx(t)}
                            className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-700 transition-colors"
                            data-testid={`pajak-del-${t.id}`} title="Hapus">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {txs.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-stone-200 bg-emerald-50/50">
                        <td colSpan={4} className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-emerald-900">
                          Total Pajak
                        </td>
                        <td className="px-4 py-3 text-right num text-base font-bold text-emerald-900"
                          data-testid="pajak-total-footer">
                          {formatRp(totalPajak)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </>
        )}

        {tab === 'laporan' && (
          <div className="space-y-6" data-testid="pajak-laporan">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-stone-900">Total PPh 22 per Bulan</h2>
              <Button variant="outline" size="sm" onClick={doExportReport}
                className="border-stone-200" data-testid="pajak-report-export">
                <Download className="w-4 h-4 mr-1" /> Export Excel
              </Button>
            </div>

            {report.length === 0 && (
              <div className="card-modern p-10 text-center text-sm text-stone-400" data-testid="pajak-report-empty">
                Belum ada data
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {report.map((m) => (
                <div key={m.ym} className="card-modern p-5" data-testid={`pajak-report-card-${m.ym}`}>
                  <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-3">
                    {monthLabel(m.ym)}
                  </div>
                  <div className="space-y-1.5 text-sm">
                    {[['Shopee', m.shopee], ['Tokopedia', m.tokopedia], ['TikTok Shop', m.tiktok], ['Lainnya', m.lainnya]].map(([label, val]) => (
                      <div key={label} className="flex justify-between">
                        <span className="text-stone-500">{label}</span>
                        <span className="num text-stone-800">{formatRp(val)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t border-stone-200 pt-2 mt-2">
                      <span className="font-semibold text-stone-900">Total</span>
                      <span className="num font-bold text-emerald-800">{formatRp(m.total)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {report.length > 0 && (
              <div className="card-modern overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px]" data-testid="pajak-report-table">
                    <thead>
                      <tr className="bg-stone-50 text-[11px] uppercase tracking-widest text-stone-500">
                        <th className="text-left px-4 py-3">Bulan</th>
                        <th className="text-right px-4 py-3">Shopee</th>
                        <th className="text-right px-4 py-3">Tokopedia</th>
                        <th className="text-right px-4 py-3">TikTok Shop</th>
                        <th className="text-right px-4 py-3">Lainnya</th>
                        <th className="text-right px-4 py-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.map((m) => (
                        <tr key={m.ym} className="border-t border-stone-100">
                          <td className="px-4 py-2.5 text-sm font-medium text-stone-800">{monthLabel(m.ym)}</td>
                          <td className="px-4 py-2.5 text-sm text-right num">{formatRp(m.shopee)}</td>
                          <td className="px-4 py-2.5 text-sm text-right num">{formatRp(m.tokopedia)}</td>
                          <td className="px-4 py-2.5 text-sm text-right num">{formatRp(m.tiktok)}</td>
                          <td className="px-4 py-2.5 text-sm text-right num">{formatRp(m.lainnya)}</td>
                          <td className="px-4 py-2.5 text-sm text-right num font-bold text-emerald-800">{formatRp(m.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editTx} onOpenChange={(o) => !o && setEditTx(null)}>
        <DialogContent className="max-w-md" data-testid="pajak-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Transaksi</DialogTitle>
            <DialogDescription>Ubah tanggal, marketplace, nomor order, atau pajak.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tanggal</Label>
                <Input type="date" value={editForm.tanggal}
                  onChange={(e) => setEditForm({ ...editForm, tanggal: e.target.value })}
                  className="bg-white" data-testid="pajak-edit-tanggal" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Marketplace</Label>
                <Select value={editForm.marketplace}
                  onValueChange={(v) => setEditForm({ ...editForm, marketplace: v })}>
                  <SelectTrigger className="bg-white" data-testid="pajak-edit-marketplace">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MARKETPLACES.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">No. Order</Label>
                <Input value={editForm.no_order}
                  onChange={(e) => setEditForm({ ...editForm, no_order: e.target.value })}
                  className="bg-white font-mono" data-testid="pajak-edit-order" />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Pajak (Rp)</Label>
                <Input type="text" inputMode="numeric" value={editForm.pajak}
                  onChange={(e) => setEditForm({ ...editForm, pajak: formatThousands(e.target.value) })}
                  className="bg-white font-mono" data-testid="pajak-edit-pajak" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTx(null)} data-testid="pajak-edit-cancel">Batal</Button>
            <Button onClick={saveEdit} className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="pajak-edit-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!delTx} onOpenChange={(o) => !o && setDelTx(null)}>
        <DialogContent className="max-w-sm" data-testid="pajak-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Hapus Transaksi</DialogTitle>
            <DialogDescription data-testid="pajak-delete-text">
              Yakin ingin menghapus transaksi ini?
            </DialogDescription>
          </DialogHeader>
          {delTx && (
            <div className="text-sm text-stone-600 bg-stone-50 border border-stone-200 rounded-md px-3 py-2 font-mono">
              {delTx.no_order} · {delTx.marketplace} · {formatRp(delTx.pajak)}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTx(null)} data-testid="pajak-delete-cancel">Batal</Button>
            <Button onClick={doDelete} className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="pajak-delete-confirm">
              <Trash2 className="w-4 h-4 mr-1" /> Hapus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportData(null); }}>
        <DialogContent className="max-w-2xl" data-testid="pajak-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Import Excel</DialogTitle>
            <DialogDescription>
              Format kolom: TANGGAL · MARKETPLACE · NO. ORDER · PAJAK. Data ditampilkan sebagai preview sebelum disimpan.
            </DialogDescription>
          </DialogHeader>

          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={pickImport}
            data-testid="pajak-import-file" />
          <Button variant="outline" onClick={() => fileRef.current?.click()}
            className="border-dashed border-stone-300 w-full h-20" data-testid="pajak-import-pick">
            <Upload className="w-4 h-4 mr-2" />
            {importData ? importData.fileName : 'Pilih file Excel (.xlsx / .xls / .csv)'}
          </Button>

          {importData && (
            <div className="space-y-3" data-testid="pajak-import-preview">
              <div className="flex gap-3 text-sm">
                <span className="px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 font-semibold"
                  data-testid="pajak-import-valid-count">
                  {importData.valid.length} baris valid
                </span>
                <span className="px-3 py-1.5 rounded-md bg-red-50 border border-red-200 text-red-800 font-semibold"
                  data-testid="pajak-import-error-count">
                  {importData.errors.length} baris error
                </span>
              </div>
              {importData.errors.length > 0 && (
                <div className="max-h-28 overflow-y-auto text-xs text-red-700 bg-red-50/60 border border-red-100 rounded-md p-3 space-y-1"
                  data-testid="pajak-import-errors">
                  {importData.errors.map((er, i) => (
                    <div key={i}>Baris {er.row}: {er.reason}</div>
                  ))}
                </div>
              )}
              <div className="max-h-56 overflow-y-auto border border-stone-200 rounded-md">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-stone-50 text-[11px] uppercase tracking-widest text-stone-500">
                      <th className="text-left px-3 py-2">Tanggal</th>
                      <th className="text-left px-3 py-2">Marketplace</th>
                      <th className="text-left px-3 py-2">No. Order</th>
                      <th className="text-right px-3 py-2">Pajak</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importData.valid.slice(0, 100).map((r, i) => (
                      <tr key={i} className="border-t border-stone-100">
                        <td className="px-3 py-1.5 num">{fmtDate(r.tanggal)}</td>
                        <td className="px-3 py-1.5">{r.marketplace}</td>
                        <td className="px-3 py-1.5 font-mono">{r.no_order}</td>
                        <td className="px-3 py-1.5 text-right num">{formatRp(r.pajak)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {importData.valid.length > 100 && (
                  <div className="text-xs text-stone-400 text-center py-2">
                    … dan {importData.valid.length - 100} baris lainnya
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportData(null); }}
              data-testid="pajak-import-cancel">
              <X className="w-4 h-4 mr-1" /> Batal
            </Button>
            <Button onClick={doImport} disabled={!importData?.valid?.length || importing}
              className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="pajak-import-submit">
              {importing ? 'Mengimpor…' : `Import Data${importData?.valid?.length ? ` (${importData.valid.length})` : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccountingLayout>
  );
}
